<script>
  import { formatDate } from "../utils/format.js";
  import { renderMarkdown } from "../utils/markdown.js";
  import ApprovalCard from "./ApprovalCard.svelte";

  // Presentational agent-run timeline, shared by the LIVE panel (AgentRunPanel)
  // and the READ-ONLY archive view. It only renders `messages` (+ an optional
  // approval card inline against its gated tool call) — it owns no polling,
  // fetching, or run state. All data-testids are preserved from the original
  // AgentRunPanel markup so specs/QA keep working.
  export let messages = [];

  // Client-side show/hide of the model's "thinking" (reasoning_content). Purely
  // presentational — content + tool calls always render regardless. Owned/
  // persisted by the live panel (AgentRunPanel); the read-only archive view can
  // pass it too since it's just a prop with a default.
  export let showReasoning = true;

  // Approval wiring. When `awaitingApproval` is set the card is rendered against
  // its gated tool call (or as a fallback below if that call isn't loaded). The
  // live panel passes it (with decision callbacks); the read-only archive view
  // leaves it false, which SUPPRESSES the ApprovalCard entirely — no card renders
  // for a historical gate (both the inline and fallback sites are gated on it).
  export let awaitingApproval = false;
  export let pendingApproval = null;
  export let deciding = false;
  export let driftBanner = false;
  export let onApprove = () => {};
  export let onReject = () => {};

  // Steering (live panel only). A PENDING steering message (steering && pending)
  // renders as a distinct "will be sent" bubble with cancel/edit affordances; a
  // DELIVERED one (pending === false) renders as a normal user message. The
  // read-only archive leaves these callbacks/flags at their no-op defaults, so no
  // affordance ever renders there. `onCancelSteering(id)` / `onEditSteering(id,
  // content)` call the DELETE / PATCH endpoints; `steeringBusyIds` disables the
  // controls on a message whose request is in flight.
  export let onCancelSteering = () => {};
  export let onEditSteering = () => {};
  export let steeringBusyIds = new Set();

  // The id of the pending steering message currently being edited inline, and its
  // working text. Only one edit is open at a time.
  let editingId = null;
  let editingText = "";

  function beginEdit(message) {
    editingId = message.id;
    editingText = message.content || "";
  }

  function cancelEdit() {
    editingId = null;
    editingText = "";
  }

  async function submitEdit(message) {
    const next = editingText.trim();
    if (!next) {
      return;
    }
    await onEditSteering(message.id, next);
    if (editingId === message.id) {
      cancelEdit();
    }
  }

  function isPendingSteering(message) {
    return message.role === "user" && message.steering && message.pending;
  }

  // Flatten a tool call's arguments JSON into [{ name, value }] rows so the
  // timeline can list every argument (name + value) in a table, not a one-liner.
  // Nested objects/arrays are dotted (row.product) / indexed (items[0]).
  function toolArgRows(argsJson) {
    if (!argsJson) {
      return [];
    }
    let parsed;
    try {
      parsed = JSON.parse(argsJson);
    } catch (_error) {
      // Non-JSON (or truncated) arguments: show the raw string as a single row.
      return [{ name: "arguments", value: argsJson }];
    }
    const rows = [];
    const walk = (value, prefix) => {
      if (value !== null && typeof value === "object") {
        const entries = Array.isArray(value)
          ? value.map((v, i) => [`${prefix}[${i}]`, v])
          : Object.entries(value).map(([k, v]) => [prefix ? `${prefix}.${k}` : k, v]);
        if (entries.length === 0) {
          rows.push({ name: prefix || "arguments", value: Array.isArray(value) ? "[]" : "{}" });
        }
        for (const [name, v] of entries) {
          walk(v, name);
        }
      } else {
        rows.push({ name: prefix || "value", value: value === null ? "null" : String(value) });
      }
    };
    walk(parsed, "");
    return rows;
  }

  // Outcome of a tool call: accepted / rejected (HITL decision), failed (tool
  // error), done (plain success), or pending (not yet executed).
  function toolCallOutcome(toolCall) {
    if (toolCall.accepted === true) return { label: "accepted", cls: "text-success-text" };
    if (toolCall.accepted === false) return { label: "rejected", cls: "text-danger-text" };
    // A human HITL rejection injects {rejected:true,...} with is_success:false —
    // show it as "rejected" (with its reason in the raw output), not a tool "failed".
    if (isRejectedResult(toolCall)) return { label: "rejected", cls: "text-danger-text" };
    if (toolCall.is_success === false) return { label: "failed", cls: "text-danger-text" };
    if (toolCall.is_success === true) return { label: "done", cls: "text-neutral-500" };
    return { label: "pending", cls: "text-neutral-400" };
  }

  function isRejectedResult(toolCall) {
    if (!toolCall.result) return false;
    try {
      return JSON.parse(toolCall.result).rejected === true;
    } catch (_error) {
      return false;
    }
  }

  // ----- Tool RESULT under each call -----
  // What the tool returned to the model: for run_sandbox this is the computed JS
  // value; for post/verify/etc. the JSON payload the model saw. A rejected/failed
  // call carries the FULL raw output (guardrail/validator text or a JS error), so
  // it's shown expanded; a plain success is collapsed by default to keep the
  // timeline compact.
  function hasResult(toolCall) {
    return toolCall.result != null && String(toolCall.result).length > 0;
  }

  function isFailedResult(toolCall) {
    return toolCall.accepted === false || toolCall.is_success === false;
  }

  // Pretty-print JSON results; fall back to the raw string for non-JSON (or an
  // already-truncated payload). Rendered as escaped text in a <pre>, never {@html}.
  function prettyResult(toolCall) {
    const raw = toolCall.result;
    if (raw == null) return "";
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch (_error) {
      return String(raw);
    }
  }
</script>

{#if messages.length === 0}
  <p class="text-xs text-neutral-500">Waiting for the agent to start working…</p>
{/if}
{#each messages as message (message.id)}
  {#if isPendingSteering(message)}
    <!-- A staged steering message: not yet delivered to the agent. Distinct
         styling + cancel/edit affordances. When the poll reports it delivered
         (pending === false) it falls through to the normal user branch below. -->
    <div
      class="bg-brand-50 border border-brand-200 border-dashed rounded-md p-2.5"
      data-testid="steering-pending"
      data-message-id={message.id}
    >
      <div class="flex items-center justify-between gap-2 mb-0.5">
        <span class="text-[0.62rem] font-bold uppercase tracking-wider text-brand-600">You · pending</span>
        <span class="text-[0.62rem] text-brand-500">will be sent to the agent</span>
      </div>

      {#if editingId === message.id}
        <textarea
          class="w-full text-xs text-neutral-800 border border-brand-200 rounded p-1.5 mt-0.5 resize-y"
          rows="2"
          bind:value={editingText}
          data-testid="steering-edit-input"
        ></textarea>
        <div class="flex items-center gap-2 mt-1.5">
          <button
            type="button"
            class="btn-primary py-0.5 px-2 text-[0.7rem]"
            data-testid="steering-edit-save"
            disabled={steeringBusyIds.has(message.id) || !editingText.trim()}
            on:click={() => submitEdit(message)}
          >
            Save
          </button>
          <button
            type="button"
            class="btn-secondary py-0.5 px-2 text-[0.7rem]"
            data-testid="steering-edit-cancel"
            on:click={cancelEdit}
          >
            Cancel
          </button>
        </div>
      {:else}
        <p class="text-xs text-neutral-800 whitespace-pre-wrap">{message.content}</p>
        <div class="flex items-center gap-3 mt-1.5">
          <button
            type="button"
            class="text-[0.7rem] font-semibold text-brand-600 hover:underline disabled:opacity-50"
            data-testid="steering-edit"
            disabled={steeringBusyIds.has(message.id)}
            on:click={() => beginEdit(message)}
          >
            Edit
          </button>
          <button
            type="button"
            class="text-[0.7rem] font-semibold text-danger-text hover:underline disabled:opacity-50"
            data-testid="steering-cancel"
            disabled={steeringBusyIds.has(message.id)}
            on:click={() => onCancelSteering(message.id)}
          >
            ✕ Cancel
          </button>
        </div>
      {/if}
    </div>
  {:else if message.role === "user"}
    <div class="bg-white border border-neutral-200 rounded-md p-2.5" data-testid="run-message-user">
      <div class="flex items-center justify-between gap-2 mb-0.5">
        <span class="text-[0.62rem] font-bold uppercase tracking-wider text-brand-600">You</span>
        <span class="text-[0.62rem] text-neutral-400">{formatDate(message.created_at)}</span>
      </div>
      <p class="text-xs text-neutral-800 whitespace-pre-wrap">{message.content}</p>
    </div>
  {:else if message.role === "assistant"}
    <div class="bg-white border border-neutral-200 rounded-md p-2.5" data-testid="run-message-assistant">
      <div class="flex items-center justify-between gap-2 mb-0.5">
        <span class="inline-flex items-center gap-1.5 min-w-0">
          <span class="text-[0.62rem] font-bold uppercase tracking-wider text-neutral-500">Agent</span>
          {#if message.model}
            <span
              class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[0.6rem] font-mono font-semibold text-neutral-500 bg-neutral-100 border border-neutral-200 truncate max-w-[16rem]"
              title={message.model}
              data-testid="run-message-model"
            >{message.model}</span>
          {/if}
          <!-- Live streaming shell: a born-hidden assistant row still in
               `processing`, surfaced by the messages endpoint while the run is
               active. It grows in place and commits to the SAME id (status flips
               to success), so this chip clears itself on the next poll. Reasoning
               with no answer yet ⇒ "thinking…"; an answer growing ⇒ "responding…".
               Historical (archive) turns are never `processing`, so no chip renders there. -->
          {#if message.status === "processing"}
            <span
              class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[0.6rem] font-semibold text-brand-600 bg-brand-50 border border-brand-200/60 animate-pulse"
              data-testid="run-message-thinking"
            >
              <span class="inline-block w-1.5 h-1.5 rounded-full bg-brand-500"></span>
              {message.content ? "responding…" : "thinking…"}
            </span>
          {/if}
        </span>
        <span class="text-[0.62rem] text-neutral-400 shrink-0">{formatDate(message.created_at)}</span>
      </div>

      {#if message.reasoning_content && showReasoning}
        <div class="mb-1.5" data-testid="run-message-reasoning">
          <span class="text-[0.6rem] font-bold uppercase tracking-wider text-neutral-400">Thinking</span>
          <!-- Reasoning shown in italics. renderMarkdown HTML-escapes the
               untrusted LLM output before any markup, so {@html} is safe. -->
          <div class="markdown-body italic text-xs text-neutral-500 mt-0.5">{@html renderMarkdown(message.reasoning_content)}</div>
        </div>
      {/if}

      {#if message.content}
        <div data-testid="run-message-answer">
          {#if message.reasoning_content}
            <span class="text-[0.6rem] font-bold uppercase tracking-wider text-neutral-400">Answer</span>
          {/if}
          <!-- Answer in normal weight. Same XSS-safe renderer. -->
          <div class="markdown-body text-xs text-neutral-800 mt-0.5">{@html renderMarkdown(message.content)}</div>
        </div>
      {/if}

      <!-- Blinking caret at the tail of a still-streaming turn once any text has
           arrived (a totally-empty shell relies on the header "thinking…" chip). -->
      {#if message.status === "processing" && (message.content || message.reasoning_content)}
        <span class="inline-block w-1 h-3.5 align-text-bottom bg-brand-400 animate-pulse" data-testid="run-stream-caret" aria-hidden="true"></span>
      {/if}

      {#if Array.isArray(message.tool_calls) && message.tool_calls.length > 0}
        <div class="mt-2 space-y-2">
          {#each message.tool_calls as toolCall (toolCall.id)}
            {@const outcome = toolCallOutcome(toolCall)}
            {@const argRows = toolArgRows(toolCall.arguments)}
            <div class="border border-neutral-200 rounded-md overflow-hidden" data-testid="run-tool-call">
              <table class="w-full border-collapse text-[0.7rem] text-left">
                <thead>
                  <tr class="bg-neutral-50 border-b border-neutral-200">
                    <th class="px-2 py-1 font-mono font-bold text-neutral-800" colspan="2" data-testid="run-tool-name">
                      {toolCall.function_name}
                    </th>
                    <th class="px-2 py-1 text-right font-bold {outcome.cls}" data-testid="run-tool-outcome">
                      {outcome.label}
                    </th>
                  </tr>
                  {#if argRows.length > 0}
                    <tr class="bg-neutral-50/60 border-b border-neutral-200 text-neutral-400">
                      <th class="px-2 py-0.5 font-semibold">Argument</th>
                      <th class="px-2 py-0.5 font-semibold" colspan="2">Value</th>
                    </tr>
                  {/if}
                </thead>
                <tbody class="divide-y divide-neutral-100">
                  {#if argRows.length === 0}
                    <tr>
                      <td class="px-2 py-1 text-neutral-400" colspan="3">(no arguments)</td>
                    </tr>
                  {:else}
                    {#each argRows as arg (arg.name)}
                      <tr data-testid="run-tool-arg">
                        <td class="px-2 py-1 font-mono text-neutral-600 align-top whitespace-nowrap">{arg.name}</td>
                        <td class="px-2 py-1 font-mono text-neutral-900 break-all" colspan="2">{arg.value}</td>
                      </tr>
                    {/each}
                  {/if}
                </tbody>
              </table>
              {#if hasResult(toolCall)}
                {@const failedResult = isFailedResult(toolCall)}
                <!-- Result the tool returned to the model. Collapsed for a plain
                     success; a rejection/failure opens with the FULL raw output. -->
                <details class="border-t border-neutral-200" open={failedResult} data-testid="run-tool-result">
                  <summary
                    class="cursor-pointer select-none px-2 py-1 text-[0.68rem] font-semibold {failedResult
                      ? 'text-danger-text bg-danger-bg/40 hover:bg-danger-bg/60'
                      : 'text-neutral-500 bg-neutral-50/60 hover:bg-neutral-100'}"
                    data-testid="run-tool-result-summary"
                  >
                    {failedResult ? "Raw output" : "Result"}
                  </summary>
                  <pre
                    class="px-2 py-1.5 text-[0.68rem] font-mono text-neutral-800 whitespace-pre-wrap break-words max-h-72 overflow-y-auto"
                    data-testid="run-tool-result-body"
                  >{prettyResult(toolCall)}</pre>
                </details>
              {/if}
            </div>
            {#if awaitingApproval && pendingApproval && pendingApproval.tool_call_id === toolCall.id}
              <ApprovalCard
                approval={pendingApproval}
                {deciding}
                {driftBanner}
                onApprove={onApprove}
                onReject={onReject}
              />
            {/if}
          {/each}
        </div>
      {/if}
    </div>
  {/if}
{/each}

<!-- Fallback: a pending approval whose gated tool_call isn't in the loaded
     window still renders its card so the gate is never hidden. -->
{#if awaitingApproval && pendingApproval && !messages.some((m) => (m.tool_calls || []).some((tc) => tc.id === pendingApproval?.tool_call_id))}
  <ApprovalCard
    approval={pendingApproval}
    {deciding}
    {driftBanner}
    onApprove={onApprove}
    onReject={onReject}
  />
{/if}
